import { useEffect, useState } from 'react'
import { localizeDigits } from '@/lib/localize-digits'

/**
 * Hook that returns a live countdown to a target date
 * Updates every second and formats as MM:SS or HH:MM:SS
 * @param targetDate - The date to count down to
 * @returns Formatted countdown string (e.g., "05:30" or "1:05:30")
 */
export function useCountdown(
  targetDate: Date | null | undefined,
): string | null {
  const [countdown, setCountdown] = useState<string | null>(null)

  useEffect(() => {
    if (!targetDate) {
      setCountdown(null)
      return
    }

    const updateCountdown = () => {
      const now = Date.now()
      const target = targetDate.getTime()
      const remainingMs = target - now

      if (remainingMs <= 0) {
        setCountdown(localizeDigits('00:00'))
        return
      }

      const totalSeconds = Math.floor(remainingMs / 1000)
      const hours = Math.floor(totalSeconds / 3600)
      const minutes = Math.floor((totalSeconds % 3600) / 60)
      const seconds = totalSeconds % 60

      // Format as HH:MM:SS if >= 1 hour, otherwise MM:SS
      if (hours > 0) {
        setCountdown(
          localizeDigits(
            `${hours}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`,
          ),
        )
      } else {
        setCountdown(
          localizeDigits(
            `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`,
          ),
        )
      }
    }

    // Initial update
    updateCountdown()

    // Update every second
    const interval = setInterval(updateCountdown, 1000)

    return () => clearInterval(interval)
  }, [targetDate])

  return countdown
}

/**
 * Hook that returns a live countup from a start date
 * Updates every second and formats as MM:SS or HH:MM:SS
 * @param startDate - The date to count up from
 * @returns Formatted countup string (e.g., "05:30" or "1:05:30")
 */
export function useCountup(startDate: Date | null | undefined): string | null {
  const [countup, setCountup] = useState<string | null>(null)

  useEffect(() => {
    if (!startDate) {
      setCountup(null)
      return
    }

    const updateCountup = () => {
      const now = Date.now()
      const start = startDate.getTime()
      const elapsedMs = now - start

      if (elapsedMs < 0) {
        setCountup(localizeDigits('00:00'))
        return
      }

      const totalSeconds = Math.floor(elapsedMs / 1000)
      const hours = Math.floor(totalSeconds / 3600)
      const minutes = Math.floor((totalSeconds % 3600) / 60)
      const seconds = totalSeconds % 60

      // Format as HH:MM:SS if >= 1 hour, otherwise MM:SS
      if (hours > 0) {
        setCountup(
          localizeDigits(
            `${hours}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`,
          ),
        )
      } else {
        setCountup(
          localizeDigits(
            `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`,
          ),
        )
      }
    }

    // Initial update
    updateCountup()

    // Update every second
    const interval = setInterval(updateCountup, 1000)

    return () => clearInterval(interval)
  }, [startDate])

  return countup
}
